import React, { useState, useEffect } from "react";
import { ReactComponent as AddIcon } from "../../../Assets/Icons/Add.svg";
import CloseIcon from "../../../Assets/Icons/CloseIconSvg.svg";
import AddCodesModal from "./AddCodesModal";
import { useDispatch, useSelector } from "react-redux";
import { getOfferCodeDetails } from "../../../services/offerService";

const ViewCodesModal = ({ onClose, codes = [], usedCount = 0, totalCount = 0, onAddCodes, offerId, merchantId }) => {
  const [showAddCodesModal, setShowAddCodesModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  
  const dispatch = useDispatch();
  
  // Get dynamic data from Redux
  const offerCodeDetailsFromRedux = useSelector(
    (state) => state?.offerReducer?.offerCodeDetails
  );
  const offerRedemptionCodeCounts = useSelector(
    (state) => state?.offerReducer?.offerRedemptionCodeCounts
  );

  // Use codes from offerRedemptionCodeCounts since they're already available there
  const dynamicCodes = offerRedemptionCodeCounts?.offerCodesList || codes;
  const dynamicUsedCount = offerRedemptionCodeCounts?.usedCodes || usedCount;
  const dynamicTotalCount = offerRedemptionCodeCounts?.totalCodes || totalCount;
  const dynamicRemainingCount = offerRedemptionCodeCounts?.remainingCodes || (dynamicTotalCount - dynamicUsedCount);
  const dynamicPercentage = offerRedemptionCodeCounts?.percentageUsed || 
    (dynamicTotalCount > 0 ? Math.round((dynamicUsedCount / dynamicTotalCount) * 100) : 0);

  // Debug logging
  console.log("ViewCodesModal - offerRedemptionCodeCounts:", offerRedemptionCodeCounts);
  console.log("ViewCodesModal - dynamicCodes:", dynamicCodes);
  console.log("ViewCodesModal - dynamicCodes length:", dynamicCodes?.length);

  // Load codes when component mounts
  useEffect(() => {
    if (offerId && merchantId) {
      loadCodes();
    }
  }, [offerId, merchantId, currentPage, statusFilter]);

  const loadCodes = async () => {
    try {
      setIsLoading(true);
      await dispatch(getOfferCodeDetails({ 
        offerId, 
        merchantId,
        page: currentPage,
        status: statusFilter
      }));
    } catch (error) {
      console.error("Error loading codes:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCodesClick = () => {
    setShowAddCodesModal(true);
  };

  const handleAddCodesModalClose = () => {
    setShowAddCodesModal(false);
  };

  const handleCodesAdded = (newCodes) => {
    // Handle the new codes here - you can pass them to parent component
    onAddCodes && onAddCodes(newCodes);
    setShowAddCodesModal(false);
    // Reload codes after adding new ones
    loadCodes();
  };

  return (
    <>
      {/* ViewCodesModal - Hide when AddCodesModal is open */}
      {!showAddCodesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative">
            {/* Close Button - Positioned outside the modal */}
            <button
              onClick={onClose}
              className="absolute -top-6 -right-6 w-[45px] h-[45px] cursor-pointer"
            >
              <img src={CloseIcon} alt="Close" className="w-[45px] h-[45px]" />
            </button>

            <div className="bg-white rounded-lg p-6 w-[90vw] max-w-lg max-h-[80vh] overflow-y-auto">
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-4">Redemption Codes</h3>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="text-base font-semibold text-gray-700">
                    {dynamicPercentage}% Used out of {dynamicTotalCount}
                  </div>
                  <button
                    onClick={handleAddCodesClick}
                    disabled={isLoading}
                    className="flex items-center gap-1 px-3 py-1.5 border border-[#00ADEF] text-[#00ADEF] hover:bg-[#E6F8FF] text-xs font-medium rounded-[4px] h-8 disabled:opacity-50"
                  >
                    <span className="text-sm font-normal">+</span>
                    Add codes
                  </button>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                  <div 
                    className="bg-yellow-400 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${dynamicPercentage}%` }}
                  ></div>
                </div>
                
                {/* Updated layout to match Figma design with dots below labels */}
                <div className="flex items-start gap-8 text-sm">
                  <div className="flex flex-col">
                    <span className="text-gray-600 mb-1">Used Codes</span>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="font-medium text-gray-800">{dynamicUsedCount}</span>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-gray-600 mb-1">Remaining Codes</span>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="font-medium text-gray-800">{dynamicRemainingCount}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Codes List Container with Clean Blue Scrollbar */}
              <div 
                className="bg-gray-50 rounded-lg p-4 max-h-[400px] overflow-y-auto codes-container"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">Loading codes...</div>
                  </div>
                ) : dynamicCodes.length > 0 ? (
                  <div className="grid grid-cols-3 gap-x-24 gap-y-2">
                    {dynamicCodes.map((code, index) => (
                      <span key={index} className="text-sm font-medium text-gray-700">
                        {typeof code === 'string' ? code : code.code || code.value || 'N/A'}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">No codes available</div>
                  </div>
                )}
              </div>

              {/* Pagination or Load More if needed */}
              {offerCodeDetailsFromRedux?.hasMore && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={isLoading}
                    className="px-4 py-2 text-sm text-[#00ADEF] border border-[#00ADEF] rounded-lg hover:bg-[#E6F8FF] disabled:opacity-50"
                  >
                    {isLoading ? "Loading..." : "Load More"}
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <style jsx global>{`
            .codes-container {
              scrollbar-width: thin;
              scrollbar-color: #00ADEF transparent;
            }
            
            .codes-container::-webkit-scrollbar {
              width: 6px;
            }
            
            .codes-container::-webkit-scrollbar-track {
              background: transparent;
            }
            
            .codes-container::-webkit-scrollbar-thumb {
              background: #00ADEF;
              border-radius: 3px;
            }
            
            .codes-container::-webkit-scrollbar-thumb:hover {
              background: #0088cc;
            }
            
            /* Completely hide scrollbar buttons/arrows */
            .codes-container::-webkit-scrollbar-button:single-button {
              display: none;
            }
            
            .codes-container::-webkit-scrollbar-button:double-button {
              display: none;
            }
            
            .codes-container::-webkit-scrollbar-button:vertical:start:decrement {
              display: none;
            }
            
            .codes-container::-webkit-scrollbar-button:vertical:end:increment {
              display: none;
            }
            
            .codes-container::-webkit-scrollbar-corner {
              background: transparent;
            }
          `}</style>
        </div>
      )}

      {/* Add Codes Modal */}
      {showAddCodesModal && (
        <AddCodesModal 
          onClose={handleAddCodesModalClose}
          onAddCodes={handleCodesAdded}
          offerId={offerId}
          merchantId={merchantId}
        />
      )}
    </>
  );
};

export default ViewCodesModal;